import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { 
  AlertTriangle, 
  CheckCircle, 
  ExternalLink, 
  Download,
  RefreshCw,
  Globe,
  LinkIcon,
  AlertCircle
} from 'lucide-react';

interface LinkCheckResult {
  url: string;
  status: number;
  statusText: string;
  isInternal: boolean;
  foundOnPages: string[];
  error?: string;
}

interface CrawlResult {
  totalPages: number;
  checkedLinks: LinkCheckResult[];
  brokenLinks: LinkCheckResult[];
  warnings: LinkCheckResult[];
  summary: {
    totalLinks: number;
    brokenLinks: number;
    warningLinks: number;
    externalLinks: number;
    internalLinks: number;
  };
}

export const LinkChecker: React.FC = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [results, setResults] = useState<CrawlResult | null>(null);
  const [progress, setProgress] = useState(0);

  const runLinkCheck = async () => {
    setIsChecking(true);
    setProgress(0);
    setResults(null);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 1000);

      const { data, error } = await supabase.functions.invoke('link-checker', {
        body: {
          action: 'check-links',
          baseUrl: window.location.origin
        }
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (error) {
        throw error;
      }

      setResults(data);
    } catch (error) {
      console.error('Link check failed:', error);
      // Handle error display here
    } finally {
      setIsChecking(false);
    }
  };

  const downloadReport = (format: 'csv' | 'json') => {
    if (!results) return;

    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'csv') {
      const csvRows = [
        'URL,Status,Status Text,Type,Found On Pages,Error'
      ];
      
      results.checkedLinks.forEach(link => {
        const foundOnPages = link.foundOnPages.join('; ');
        csvRows.push(
          `"${link.url}",${link.status},"${link.statusText}","${link.isInternal ? 'Internal' : 'External'}","${foundOnPages}","${link.error || ''}"`
        );
      });
      
      content = csvRows.join('\n');
      filename = `link-check-${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    } else {
      content = JSON.stringify(results, null, 2);
      filename = `link-check-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: number) => {
    if (status === 0) {
      return <Badge variant="destructive">Error</Badge>;
    } else if (status >= 400) {
      return <Badge variant="destructive">{status}</Badge>;
    } else if (status >= 300) {
      return <Badge variant="secondary">{status}</Badge>;
    } else {
      return <Badge variant="outline" className="border-green-500 text-green-700">{status}</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Link Checker</h1>
          <p className="text-muted-foreground">Crawl your entire site and identify broken links</p>
        </div>
        <Button onClick={runLinkCheck} disabled={isChecking}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isChecking ? 'animate-spin' : ''}`} />
          {isChecking ? 'Checking Links...' : 'Start Link Check'}
        </Button>
      </div>

      {isChecking && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Crawling site and checking links...</span>
              </div>
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-muted-foreground">
                This may take a few minutes depending on the size of your site.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {results && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-blue-600">{results.totalPages}</div>
                <div className="text-sm text-muted-foreground">Pages Crawled</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold">{results.summary.totalLinks}</div>
                <div className="text-sm text-muted-foreground">Total Links</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-red-600">{results.summary.brokenLinks}</div>
                <div className="text-sm text-muted-foreground">Broken Links</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-amber-600">{results.summary.warningLinks}</div>
                <div className="text-sm text-muted-foreground">Warnings</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {results.summary.totalLinks - results.summary.brokenLinks}
                </div>
                <div className="text-sm text-muted-foreground">Working Links</div>
              </CardContent>
            </Card>
          </div>

          {/* Download Options */}
          <Card>
            <CardHeader>
              <CardTitle>Export Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => downloadReport('csv')}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV Report
                </Button>
                <Button variant="outline" onClick={() => downloadReport('json')}>
                  <Download className="h-4 w-4 mr-2" />
                  Download JSON Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Broken Links */}
          {results.brokenLinks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Broken Links ({results.brokenLinks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {results.brokenLinks.map((link, index) => (
                    <Alert key={index} variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{link.url}</span>
                            {getStatusBadge(link.status)}
                            {!link.isInternal && <Globe className="h-3 w-3" />}
                          </div>
                          <div className="text-sm">
                            <strong>Status:</strong> {link.statusText}
                            {link.error && <span className="text-red-600"> - {link.error}</span>}
                          </div>
                          <div className="text-sm">
                            <strong>Found on pages:</strong> {link.foundOnPages.slice(0, 3).join(', ')}
                            {link.foundOnPages.length > 3 && ` (+${link.foundOnPages.length - 3} more)`}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {results.warnings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Warnings ({results.warnings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {results.warnings.map((link, index) => (
                    <Alert key={index}>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{link.url}</span>
                            {getStatusBadge(link.status)}
                            {!link.isInternal && <Globe className="h-3 w-3" />}
                          </div>
                          <div className="text-sm">
                            <strong>Status:</strong> {link.statusText} (Redirect)
                          </div>
                          <div className="text-sm">
                            <strong>Found on:</strong> {link.foundOnPages.slice(0, 2).join(', ')}
                            {link.foundOnPages.length > 2 && ` (+${link.foundOnPages.length - 2} more)`}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success Message */}
          {results.brokenLinks.length === 0 && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium">Great news! No broken links found.</div>
                <div className="text-sm mt-1">
                  All {results.summary.totalLinks} links on your site are working properly.
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Link Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="h-5 w-5" />
                All Links Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Internal Links</h4>
                  <p className="text-sm text-muted-foreground">
                    {results.summary.internalLinks} links pointing to your own site
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">External Links</h4>
                  <p className="text-sm text-muted-foreground">
                    {results.summary.externalLinks} links pointing to external sites
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LinkChecker;